import {
  Card,
  Expandable,
  expandableChevronSx,
  Input,
  Text,
} from '@components/ui';
import type { RatingDamage } from '@features/buyer/jobs/criteria-option';
import CriteriaOption from '@features/buyer/jobs/criteria-option/criteria-option';
import { useRatingCategoriesQuery, useRatingDamagesQuery } from '@lib/api/rating';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import {
  type ReactNode,
  useDeferredValue,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import { RatingResultBadge } from './rating-result-badge';
import {
  type CriteriaResult,
  criteriaResult,
  isIssue,
  type RatedCriteria,
  RATING_SCALE_MAX,
  summarizeRatings,
} from './ratingSummary';

type ResultFilter = 'all' | 'issues' | 'notRated';

const RESULT_FILTERS: readonly ResultFilter[] = ['all', 'issues', 'notRated'];

const NO_CRITERIA: RatedCriteria[] = [];

interface ResultRow {
  criteria: RatedCriteria;
  result: CriteriaResult;
}

const matchesFilter = (row: ResultRow, filter: ResultFilter): boolean => {
  switch (filter) {
    case 'all':
      return true;
    case 'issues':
      return isIssue(row.result);
    case 'notRated':
      return row.result.kind === 'notRated';
    default: {
      const exhaustive: never = filter;
      return exhaustive;
    }
  }
};

const SummaryTile = ({
  label,
  value,
  hint,
  children,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  children?: ReactNode;
}): JSX.Element => (
  <Box
    sx={{
      minWidth: 0,
      borderRadius: 2,
      bgcolor: 'action.hover',
      px: 2,
      py: 1.5,
    }}
  >
    <Text as="span" variant="overline" tone="muted" sx={{ display: 'block' }}>
      {label}
    </Text>
    <Box
      sx={{
        mt: 0.5,
        fontSize: 24,
        fontWeight: 700,
        lineHeight: 1.2,
        fontVariantNumeric: 'tabular-nums',
        color: 'text.primary',
      }}
    >
      {value}
    </Box>
    {hint ? (
      <Text variant="caption" tone="muted" sx={{ display: 'block', mt: 0.25 }}>
        {hint}
      </Text>
    ) : null}
    {children}
  </Box>
);

const ScoreMeter = ({ score }: { score: number }): JSX.Element => (
  <Box
    role="presentation"
    sx={{
      mt: 1,
      height: 4,
      borderRadius: 999,
      bgcolor: 'divider',
      overflow: 'hidden',
    }}
  >
    <Box
      sx={{
        height: '100%',
        width: `${(score / RATING_SCALE_MAX) * 100}%`,
        borderRadius: 'inherit',
        bgcolor: score >= RATING_SCALE_MAX ? 'success.main' : 'warning.main',
      }}
    />
  </Box>
);

const filterChipSx = (selected: boolean) => ({
  height: 32,
  px: 1.5,
  borderRadius: 999,
  border: 1,
  borderColor: selected ? 'text.primary' : 'divider',
  bgcolor: selected ? 'text.primary' : 'transparent',
  color: selected ? 'background.paper' : 'text.secondary',
  fontSize: 13,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
  transition: 'background-color 0.15s ease, color 0.15s ease',
  '&:hover': selected ? {} : { color: 'text.primary', bgcolor: 'action.hover' },
});

const matchesLabelSearch = (row: ResultRow, needle: string): boolean => {
  if (!needle) return true;
  return (row.criteria.title ?? '').toLowerCase().includes(needle);
};

export const RatingResults = ({ jobId }: { jobId: string }): JSX.Element => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState<ResultFilter>('all');
  const [search, setSearch] = useState('');
  const searchNeedle = useDeferredValue(search.trim().toLowerCase());
  const [openId, setOpenId] = useState<string | number | null>(null);
  const { data, isLoading } = useRatingCategoriesQuery(jobId);
  const criteria: RatedCriteria[] = data?.ratingCriterias ?? NO_CRITERIA;
  const { data: damageGroups } = useRatingDamagesQuery(
    openId == null ? undefined : String(openId),
    jobId
  );
  const openDamages = (damageGroups?.[0] as { damages?: RatingDamage[] })
    ?.damages;

  const rows = useMemo<ResultRow[]>(
    () =>
      criteria.map((item) => ({
        criteria: item,
        result: criteriaResult(item.options ?? []),
      })),
    [criteria]
  );
  const matchedRows = useMemo(
    () => rows.filter((row) => matchesLabelSearch(row, searchNeedle)),
    [rows, searchNeedle]
  );
  const summary = useMemo(
    () => summarizeRatings(rows.map((row) => row.result)),
    [rows]
  );
  const matchedSummary = useMemo(
    () => summarizeRatings(matchedRows.map((row) => row.result)),
    [matchedRows]
  );
  const counts: Record<ResultFilter, number> = {
    all: matchedRows.length,
    issues: matchedSummary.issues,
    notRated: matchedSummary.total - matchedSummary.checked,
  };
  const visibleFilters = RESULT_FILTERS.filter(
    (item) => item === 'all' || counts[item] > 0
  );
  const visibleRows = matchedRows.filter((row) => matchesFilter(row, filter));

  useEffect(() => {
    if (
      openId != null &&
      !visibleRows.some((row) => row.criteria.id === openId)
    ) {
      setOpenId(null);
    }
  }, [openId, visibleRows]);

  return (
    <Box component="section" aria-labelledby="rating-results-title">
      <Card>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
          }}
        >
          <Text
            as="h2"
            id="rating-results-title"
            variant="subtitle"
            sx={{ fontWeight: 700 }}
          >
            {t('ratingDetail.results')}
          </Text>
          {!isLoading && summary.total > 0 ? (
            <Text variant="caption" tone="muted">
              {t('ratingDetail.checkedOf', {
                checked: summary.checked,
                total: summary.total,
              })}
            </Text>
          ) : null}
        </Box>

        {isLoading ? (
          <Box sx={{ display: 'grid', gap: 1 }}>
            <Skeleton variant="rounded" height={96} />
            {Array.from({ length: 6 }, (_, index) => (
              <Skeleton key={index} variant="rounded" height={48} />
            ))}
          </Box>
        ) : rows.length === 0 ? (
          <Text tone="muted" sx={{ fontSize: 14 }}>
            {t('ratingDetail.noResults')}
          </Text>
        ) : (
          <>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(3, minmax(0, 1fr))',
                },
                gap: 1.5,
                mb: 2.5,
              }}
            >
              <SummaryTile
                label={t('ratingDetail.conditionScore')}
                value={
                  summary.averageScore == null ? (
                    '—'
                  ) : (
                    <>
                      {summary.averageScore.toFixed(1)}
                      <Box
                        component="span"
                        sx={{ ml: 0.5, fontSize: 14, color: 'text.secondary' }}
                      >
                        / {RATING_SCALE_MAX}
                      </Box>
                    </>
                  )
                }
                hint={
                  summary.averageScore == null
                    ? t('ratingDetail.noScore')
                    : undefined
                }
              >
                {summary.averageScore == null ? null : (
                  <ScoreMeter score={summary.averageScore} />
                )}
              </SummaryTile>
              <SummaryTile
                label={t('ratingDetail.issuesFound')}
                value={
                  <Box
                    component="span"
                    sx={{
                      color:
                        summary.issues > 0 ? 'warning.main' : 'success.main',
                    }}
                  >
                    {summary.issues}
                  </Box>
                }
                hint={t(
                  summary.issues > 0
                    ? 'ratingDetail.needsAttention'
                    : 'ratingDetail.noIssues'
                )}
              />
              <SummaryTile
                label={t('ratingDetail.itemsChecked')}
                value={
                  <>
                    {summary.checked}
                    <Box
                      component="span"
                      sx={{ ml: 0.5, fontSize: 14, color: 'text.secondary' }}
                    >
                      / {summary.total}
                    </Box>
                  </>
                }
              />
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { sm: 'center' },
                gap: 1.5,
                mb: 1.5,
              }}
            >
              <Box role="search" sx={{ flex: 1, minWidth: 0 }}>
                <Input
                  type="text"
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Escape') setSearch('');
                  }}
                  placeholder={t('ratingDetail.searchPlaceholder')}
                  inputProps={{
                    'aria-label': t('ratingDetail.search'),
                    enterKeyHint: 'search',
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchRoundedIcon
                          sx={{ fontSize: 20, color: 'text.secondary' }}
                        />
                      </InputAdornment>
                    ),
                    endAdornment: search ? (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          edge="end"
                          aria-label={t('ratingDetail.clearSearch')}
                          onClick={() => setSearch('')}
                        >
                          <CloseRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                />
              </Box>
              {visibleFilters.length > 1 ? (
                <Box
                  role="group"
                  aria-label={t('ratingDetail.filterLabel')}
                  sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}
                >
                  {visibleFilters.map((item) => (
                    <ButtonBase
                      key={item}
                      aria-pressed={filter === item}
                      onClick={() => setFilter(item)}
                      sx={filterChipSx(filter === item)}
                    >
                      {t(`ratingDetail.filters.${item}`)} · {counts[item]}
                    </ButtonBase>
                  ))}
                </Box>
              ) : null}
            </Box>

            {visibleRows.length === 0 ? (
              <Text tone="muted" sx={{ fontSize: 14, py: 2 }}>
                {searchNeedle
                  ? t('ratingDetail.emptySearch', { query: search.trim() })
                  : t('ratingDetail.noFilteredResults')}
              </Text>
            ) : (
              <Box
                component="ul"
                sx={{
                  listStyle: 'none',
                  m: 0,
                  p: 0,
                  border: 1,
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                }}
              >
                {visibleRows.map(({ criteria: item, result }) => {
                  const open = openId === item.id;
                  const panelId = `rating-criteria-${item.id}`;
                  return (
                    <Box
                      component="li"
                      key={item.id}
                      sx={{
                        '&:not(:first-of-type)': {
                          borderTop: 1,
                          borderColor: 'divider',
                        },
                      }}
                    >
                      <ButtonBase
                        aria-expanded={open}
                        aria-controls={panelId}
                        onClick={() => setOpenId(open ? null : item.id)}
                        sx={{
                          width: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1.5,
                          px: 2,
                          py: 1.25,
                          minHeight: 48,
                          textAlign: 'left',
                          bgcolor: open ? 'action.hover' : 'transparent',
                          '&:hover': { bgcolor: 'action.hover' },
                          '&.Mui-focusVisible': { bgcolor: 'action.focus' },
                        }}
                      >
                        <Text
                          as="span"
                          sx={{
                            flex: 1,
                            minWidth: 0,
                            fontSize: 14,
                            fontWeight: 500,
                          }}
                        >
                          {item.title}
                        </Text>
                        <RatingResultBadge result={result} />
                        <ExpandMoreRounded
                          aria-hidden
                          sx={[
                            { fontSize: 20, color: 'text.secondary' },
                            expandableChevronSx(open),
                          ]}
                        />
                      </ButtonBase>
                      <Expandable open={open}>
                        <Box
                          id={panelId}
                          sx={{
                            px: 2,
                            pt: 0.5,
                            pb: 2,
                            bgcolor: 'action.hover',
                          }}
                        >
                          <CriteriaOption
                            ratingCriteriaOptions={item.options ?? []}
                            damages={openDamages}
                            ratingCriteria={item}
                            includeCommonImages={item.includeCommonImages}
                            includeCriteriaNotes={item.includeCriteriaNotes}
                          />
                        </Box>
                      </Expandable>
                    </Box>
                  );
                })}
              </Box>
            )}
          </>
        )}
      </Card>
    </Box>
  );
};
